export enum UserRole {
  STUDENT = 'STUDENT',
  INSTRUCTOR = 'INSTRUCTOR',
  ADMIN = 'ADMIN'
}

/** Roles considered "staff" — instructors and admins. Add a new role here
 *  to flow through every staff-only filter (UsersPanel, marketing instructor
 *  picker, etc.) without grepping the codebase. */
export const STAFF_ROLES: ReadonlySet<UserRole> = new Set([
  UserRole.INSTRUCTOR,
  UserRole.ADMIN,
]);

export enum LessonType {
  VIDEO = 'VIDEO',
  TEXT = 'TEXT',
  QUIZ = 'QUIZ',
  ASSIGNMENT = 'ASSIGNMENT',
  HEADING = 'HEADING'
}

// --- PAGE-BASED LESSON CONTENT ---

export type PageType = 'text' | 'video' | 'embed' | 'quiz' | 'code' | 'image' | 'divider';

export type EmbedProvider =
  | 'youtube'
  | 'vimeo'
  | 'loom'
  | 'codepen'
  | 'codesandbox'
  | 'figma'
  | 'notion'
  | 'miro'
  | 'typeform'
  | 'google-slides'
  | 'custom';

export interface TextPageData {
  type: 'text';
  html: string; // Rich text HTML from Tiptap
}

export interface VideoPageData {
  type: 'video';
  url: string;
  provider: 'youtube' | 'vimeo' | 'loom' | 'mux' | 'custom';
}

export interface EmbedPageData {
  type: 'embed';
  provider: EmbedProvider;
  embedUrl: string;
  config?: Record<string, unknown>;
}

export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswerIndex: number;
}

export interface QuizPageData {
  type: 'quiz';
  questions: QuizQuestion[];
  passingScore: number;
}

export interface CodePageData {
  type: 'code';
  code: string;
  language: string;
  runnable: boolean;
}

export interface ImagePageData {
  type: 'image';
  url: string;
  alt: string;
  caption?: string;
  unsplashId?: string;
  unsplashAuthor?: string;
  unsplashAuthorUrl?: string;
}

export interface DividerPageData {
  type: 'divider';
  style: 'line' | 'space' | 'dots';
}

export type PageData =
  | TextPageData
  | VideoPageData
  | EmbedPageData
  | QuizPageData
  | CodePageData
  | ImagePageData
  | DividerPageData;

export interface Page {
  id: string;
  type: PageType;
  data: PageData;
  order: number;
}

export interface LessonContent {
  pages: Page[];
}

// --- SLIDE-BASED LESSON CONTENT (v2) ---
// Re-export slide types for convenience
export type { SlideBasedContent, Slide, SlideData, SlideTemplate, ThemeId, Theme } from './components/course-builder/slides/slideTypes';
export { isSlideBasedContent, createEmptySlideContent, themes } from './components/course-builder/slides/slideTypes';
export { createSlide } from './components/course-builder/slides/slideRegistry';

// Helper to create empty page content
export const createEmptyLessonContent = (): LessonContent => ({
  pages: []
});

// Helper to create a new page
export const createPage = (type: PageType, order: number): Page => {
  const id = crypto.randomUUID();

  const defaultData: Record<PageType, PageData> = {
    text: { type: 'text', html: '' },
    video: { type: 'video', url: '', provider: 'youtube' },
    embed: { type: 'embed', provider: 'custom', embedUrl: '' },
    quiz: { type: 'quiz', questions: [], passingScore: 70 },
    code: { type: 'code', code: '', language: 'javascript', runnable: false },
    image: { type: 'image', url: '', alt: '' },
    divider: { type: 'divider', style: 'line' }
  };

  return {
    id,
    type,
    data: defaultData[type],
    order
  };
};

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatarUrl?: string;
  tenantId: string;
  isSuperAdmin?: boolean;
}

// Import SlideBasedContent type
import type { SlideBasedContent } from './components/course-builder/slides/slideTypes';

export interface Lesson {
  id: string;
  sectionId: string;
  title: string;
  type: LessonType;
  duration: number; // in minutes
  content: LessonContent | SlideBasedContent | string; // Slide-based (v2), page-based (v1), or legacy string
  /**
   * Frozen snapshot served to students (working-draft vs published-snapshot
   * model). `undefined` when not loaded; `null` when never published (draft-only).
   * Populated on the builder's draft read (getCourseById default); student reads
   * fold this into `content` instead of surfacing it separately.
   */
  publishedContent?: LessonContent | SlideBasedContent | string | null;
  isCompleted: boolean; // Static flag for initial state (can be overridden by context)
  isLocked: boolean;
  order: number;
}

// Type guard to check if content is new page-based format
export const isPageBasedContent = (content: LessonContent | SlideBasedContent | string): content is LessonContent => {
  return typeof content === 'object' && content !== null && 'pages' in content;
};

export interface Section {
  id: string;
  courseId: string;
  title: string;
  order: number;
  lessons: Lesson[];
}

export interface Course {
  id: string;
  instructorId: string; // New field for ownership
  title: string;
  description: string;
  thumbnailUrl: string;
  instructorName: string;
  price: number;
  category: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  sections: Section[];
  totalStudents: number;
  rating: number;
  points: number;
  courseType?: CourseType;
  published?: boolean;
  certificateEnabled?: boolean;
  /**
   * Essay markings a learner gets on enrolling, and how many of them may go on
   * any one exam question. Set only on courses that carry essay-marking slides
   * — that is also how the system tells those courses apart from the rest.
   */
  markingsIncluded?: number | null;
  markingsPerQuestion?: number | null;
  /** Badge id awarded when a learner completes this course (FK -> badges.id). */
  completionBadgeId?: string;
  /**
   * Show the facilitator (name + avatar) on the course detail page. Default
   * OFF: on single-facilitator tenants the row is dead metadata, so a course
   * opts in rather than every course opting out.
   */
  showFacilitator?: boolean;
  tags?: Tag[];
  sortOrder?: number;
  /**
   * Whether `sections[].lessons[].content` was actually loaded.
   *
   * `false` means this copy carries lesson **metadata only** — the `content`
   * JSONB (the app's heaviest payload) was deliberately left out of the query.
   * `getInstructorCourses` is the one read that does this, because every list
   * surface it feeds reads `sections.length` and nothing deeper. Anything that
   * needs to *render* a lesson must refetch via
   * `getCourseById(id, { content: 'draft' })` when it sees `false`.
   *
   * Absent/`true` means content is present. Treat `undefined` as loaded — the
   * flag is an opt-in marker on the light read, not a required field.
   */
  contentLoaded?: boolean;
}

export interface Enrollment {
  courseId: string;
  progress: number; // 0-100
  lastAccessedLessonId?: string;
  completedLessonIds: string[];
  enrolledAt: Date;
  transactionId?: string;
  paymentStatus?: 'free' | 'paid' | 'refunded';
  /** Time-limited access (bundle-granted). Undefined = lifetime. */
  expiresAt?: Date;
  /** Bundle this enrollment was granted through, if any. */
  sourceBundleId?: string;
}

// --- BUNDLES ---

/** Lightweight course info embedded on a bundle for cards/detail pages. */
export interface BundleCourseSummary {
  id: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  sortOrder: number;
  /** The member's own standalone price — used to anchor the bundle's discount. */
  price: number;
}

export interface Bundle {
  id: string;
  title: string;
  description: string;
  thumbnailUrl?: string;
  price: number;
  /** Days of access granted on enrollment. Undefined = lifetime. */
  accessDurationDays?: number;
  published: boolean;
  sortOrder: number;
  courses: BundleCourseSummary[];
  createdBy?: string;
}

// --- EMBED INTERACTION TRACKING ---

export type EmbedStatus = 'not_started' | 'started' | 'in_progress' | 'completed';
export type EmbedSubmissionType = 'code' | 'text' | 'file' | 'form' | 'custom';

export interface EmbedInteraction {
  id: string;
  enrollmentId: string;
  lessonId: string;
  slideId: string;
  embedUrl: string;
  status: EmbedStatus;
  progress: number;
  score?: number;
  timeSpentSeconds: number;
  submissionData?: unknown;
  submissionType?: EmbedSubmissionType;
  startedAt?: Date;
  completedAt?: Date;
  lastInteractionAt: Date;
}

// --- ACTIVITY RESPONSE TRACKING ---

export type { ActivityType, ActivityMode, ActivitySlideData } from './components/course-builder/slides/slideTypes';

export interface ActivityResponsePayload {
  activityType: string;
  mode: 'survey' | 'quiz';
  responseData: Record<string, unknown>;
  isCorrect?: boolean;
  score?: number;
}

export interface ActivityResponse {
  id: string;
  enrollmentId: string;
  lessonId: string;
  slideId: string;
  activityType: string;
  mode: string;
  responseData: Record<string, unknown>;
  isCorrect?: boolean;
  score?: number;
  submittedAt: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface StudentActivityResponse {
  responseId: string;
  courseId: string;
  courseTitle: string;
  lessonId: string;
  lessonTitle: string;
  slideId: string;
  activityType: string;
  mode: string;
  responseData: Record<string, unknown>;
  isCorrect?: boolean;
  score?: number;
  submittedAt: Date;
}

export interface CourseActivityResponse {
  responseId: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  lessonId: string;
  lessonTitle: string;
  lessonOrder: number;
  sectionTitle: string;
  sectionOrder: number;
  slideId: string;
  activityType: string;
  mode: string;
  responseData: Record<string, unknown>;
  isCorrect?: boolean;
  score?: number;
  submittedAt: Date;
}

// --- EMBED MESSAGE PROTOCOL ---
// Protocol for communication between embedded webapps and the LMS

export interface EmbedMessage {
  type: 'lms-embed-event';
  version: '1.0';
  payload: EmbedEventPayload;
}

export type EmbedEventPayload =
  | { event: 'ready' }
  | { event: 'started' }
  | { event: 'progress'; percent: number }
  // `result` is validated by parseEmbedResult (lib/embedProtocol.ts) and
  // recorded per attempt in embed_results; `score` stays the 0-100 summary.
  | { event: 'completed'; score?: number; result?: unknown; data?: unknown }
  // A moment mid-run, as it happens: validated by parseEmbedCheckpoint
  // (lib/embedProtocol.ts) and recorded as one learning_interactions row per
  // (attempt, step). `result` is the same shape as completed's; `data` is the
  // app's own small record of what the learner chose or wrote.
  | { event: 'checkpoint'; step?: unknown; attemptId?: unknown; result?: unknown; data?: unknown }
  // The learner asked to leave — sent from the embed's own final button, so the
  // exit lives where they are looking instead of only in the lesson chrome.
  // Deliberately distinct from 'completed': that fires on ARRIVING at the close
  // (and gates progress), this fires on CHOOSING to move on. Collapsing the two
  // yanks the learner off their own results screen before they've read it.
  | { event: 'advance' }
  | { event: 'submitted'; submission: SubmissionData }
  | { event: 'error'; message: string };

export interface SubmissionData {
  type: EmbedSubmissionType;
  content: string | object;
  metadata?: Record<string, unknown>;
}

// Message sent from LMS to embedded webapp
export interface LmsInitMessage {
  type: 'lms-init';
  version: '1.0';
  payload: {
    userId?: string;
    lessonId: string;
    slideId: string;
    previousState?: unknown;
    config?: Record<string, unknown>;
  };
}

// Type guard for embed messages
export const isEmbedMessage = (data: unknown): data is EmbedMessage => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    (data as EmbedMessage).type === 'lms-embed-event' &&
    'version' in data &&
    (data as EmbedMessage).version === '1.0' &&
    'payload' in data
  );
};

// --- PAYMENT SYSTEM TYPES ---

export enum TransactionStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
  REFUNDED = 'refunded'
}

export interface PaymentGateway {
  id: string;
  name: string;           // 'paystack', 'payfast', 'stripe'
  displayName: string;    // 'Paystack', 'PayFast', 'Stripe'
  isActive: boolean;
  config: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export interface Transaction {
  id: string;
  userId: string;
  courseId: string;
  gatewayId: string;
  amount: number;
  currency: string;
  status: TransactionStatus;
  gatewayReference?: string;
  gatewayResponse?: Record<string, any>;
  createdAt: Date;
  completedAt?: Date;
}

// Payment initialization request/response types
export interface InitializePaymentRequest {
  courseId?: string;
  bundleId?: string;
  gateway?: string;  // defaults to 'paystack'
}

export interface InitializeGuestPaymentRequest {
  courseId: string;
  gateway?: string;  // defaults to 'paystack'
  email: string;     // guest email for payment
}

export interface InitializePaymentResponse {
  authorizationUrl: string;
  reference: string;
  transactionId: string;
}

// Payment verification request/response types
export interface VerifyPaymentRequest {
  reference: string;
}

export interface VerifyPaymentResponse {
  status: 'success' | 'failed' | 'pending';
  courseId?: string;
  bundleId?: string;
  message: string;
  transaction?: Transaction;
  isGuest?: boolean;
  guestEmail?: string;
  transactionId?: string;
}

// --- ADMIN TYPES ---

export interface PlatformStats {
  totalUsers: number;
  totalStudents: number;
  totalInstructors: number;
  totalAdmins: number;
  totalCourses: number;
  publishedCourses: number;
  totalEnrollments: number;
  totalRevenue: number;
  totalTransactions: number;
  completedTransactions: number;
  pendingTransactions: number;
  failedTransactions: number;
  totalPointsAwarded: number;
}

// Hub (super-admin) types
export interface HubPlatformStats {
  totalTenants: number;
  totalUsers: number;
  totalStudents: number;
  totalInstructors: number;
  totalAdmins: number;
  totalCourses: number;
  publishedCourses: number;
  totalEnrollments: number;
  totalRevenue: number;
  totalTransactions: number;
}

export interface TenantBreakdown {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  logoUrl: string | null;
  userCount: number;
  studentCount: number;
  courseCount: number;
  enrollmentCount: number;
  revenue: number;
  avgProgress: number;
  completionRate: number;
  createdAt: string;
}

export interface HubPlatformHealth {
  failedProvisioning: number;
  emailFailures24h: number;
  orphanedReservations: number;
}

export interface HubTenantAICredits {
  tenantId: string;
  balance: number;
  burn7d: number;
  burn30d: number;
  plan: string | null;
  creditAllowance: number | null;
  periodEnd: string | null;
  sparkline: number[];
}

export interface HubAICreditPlatform {
  totalBurned30d: number;
  totalGranted: number;
  tenantsNearZero: number;
  sparkline: number[];
}

export interface HubAICreditSummary {
  platform: HubAICreditPlatform;
  tenants: HubTenantAICredits[];
}

export interface HubTenantActiveUsers {
  tenantId: string;
  dau: number;
  wau: number;
  mau: number;
  sparkline: number[];
}

export interface HubActiveUsersPlatform {
  dau: number;
  wau: number;
  mau: number;
  sparkline: number[];
}

export interface HubActiveUsersSummary {
  platform: HubActiveUsersPlatform;
  tenants: HubTenantActiveUsers[];
}

export interface HubTenantRevenue {
  tenantId: string;
  revenue30d: number;
  sparkline: number[];
}

export interface HubRevenuePlatform {
  currentWeek: number;
  previousWeek: number;
  total30d: number;
  sparkline: number[];
}

export interface HubRevenueTrend {
  platform: HubRevenuePlatform;
  tenants: HubTenantRevenue[];
}

export interface AdminSettings {
  paymentMode: 'test' | 'live';
  paystackTestPublicKey: string;
  paystackTestSecretKey: string;  // Masked for display
  paystackLivePublicKey: string;
  paystackLiveSecretKey: string;  // Masked for display
  webhookSecret: string;          // Masked for display
  webhookUrl: string;
  /**
   * Whether this tenant has usable Paystack keys of its own. False means it
   * cannot take payments at all — there is deliberately no fallback to another
   * tenant's account.
   */
  usingOwnAccount: boolean;
}

export interface CourseWithStats extends Course {
  published: boolean;
  enrollmentCount: number;
  avgProgress: number;
  completionRate: number;
  revenue: number;
  /** Latest of the course row's and its lessons' updated_at. */
  lastEditedAt: Date;
}

export interface EnrollmentWithDetails {
  enrollmentId: string;
  userId: string;
  userName: string;
  userEmail: string;
  courseId: string;
  courseTitle: string;
  instructorName: string;
  progress: number;
  paymentStatus: string;
  enrolledAt: Date;
  lastAccessedAt: Date | null;
}

export interface TransactionWithDetails {
  transactionId: string;
  userId: string | null;
  userName: string;
  userEmail: string;
  courseId: string | null;
  bundleId: string | null;
  schoolPassId: string | null;
  /** Course title, or a "Bundle: …" / "School pass: …" label for non-course items. */
  courseTitle: string;
  amount: number;
  currency: string;
  status: string;
  gatewayReference: string;
  createdAt: Date;
  completedAt: Date | null;
}

export interface TransactionFilters {
  status?: TransactionStatus;
  courseId?: string;
  userId?: string;
  startDate?: Date;
  endDate?: Date;
}

export interface UserWithStats extends User {
  enrolledCoursesCount: number;
  createdAt: Date;
}

// --- LEAD CAPTURE TYPES ---

export type LeadSource = 'free_course' | 'taster' | 'newsletter' | 'checkout' | 'utility_page' | 'lead_magnet' | 'utility_bridge';

export interface Lead {
  id: string;
  email: string;
  name?: string;
  source: LeadSource;
  sourceCourseId?: string;
  convertedUserId?: string;
  createdAt: Date;
  convertedAt?: Date;
}

export interface CreateLeadRequest {
  email: string;
  name?: string;
  source: LeadSource;
  sourceCourseId?: string;
}

export interface CreateLeadResponse {
  lead: Lead;
  isNewLead: boolean;
  accessToken?: string; // For temporary course access
}

// --- UTILITY PAGE TYPES ---

// --- REPORTING & GRADING TYPES ---

export interface StudentPerformance {
  enrollmentId: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  courseId: string;
  courseTitle: string;
  instructorName?: string;
  progress: number;
  completedLessons: number;
  totalLessons: number;
  enrolledAt: Date;
  paymentStatus: string;
}

export interface InstructorCourseAnalytics {
  courseId: string;
  courseTitle: string;
  instructorName?: string;
  totalEnrollments: number;
  avgProgress: number;
  completionRate: number;
  totalLessons: number;
  totalRevenue: number;
  isPublished: boolean;
}

export interface StudentGradebookEntry {
  courseId: string;
  courseTitle: string;
  instructorName: string;
  progress: number;
  completedLessons: number;
  totalLessons: number;
  quizScores: Array<{
    lessonId: string;
    score: number;
    passed: boolean;
    submittedAt: string;
  }>;
  embedScores: Array<{
    lessonId: string;
    slideId: string;
    score: number;
    status: string;
    completedAt: string | null;
  }>;
  enrolledAt: Date;
}

export interface InstructorSubmission {
  interactionId: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  courseId: string;
  courseTitle: string;
  instructorName?: string;
  lessonId: string;
  lessonTitle: string;
  slideId: string;
  embedUrl: string;
  status: string;
  score: number | null;
  submissionData: unknown;
  submissionType: string;
  submittedAt: Date;
  timeSpentSeconds: number;
}

// --- UNIFIED SUBMISSIONS (v2) ---

export type SubmissionStatusType = 'pending' | 'graded' | 'returned';

export interface Submission {
  id: string;
  enrollmentId: string;
  lessonId: string;
  slideId: string;
  activityResponseId?: string;
  embedInteractionId?: string;
  submissionType: string;
  submissionData: unknown;
  score: number | null;
  feedback: string | null;
  status: SubmissionStatusType;
  gradedBy?: string;
  gradedAt: Date | null;
  submittedAt: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface InstructorSubmissionV2 {
  submissionId: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  courseId: string;
  courseTitle: string;
  instructorName?: string;
  lessonId: string;
  lessonTitle: string;
  slideId: string;
  submissionType: string;
  submissionData: unknown;
  score: number | null;
  feedback: string | null;
  status: SubmissionStatusType;
  gradedAt: Date | null;
  submittedAt: Date;
}

export interface StudentSubmission {
  submissionId: string;
  courseId: string;
  courseTitle: string;
  lessonId: string;
  lessonTitle: string;
  slideId: string;
  submissionType: string;
  submissionData: unknown;
  score: number | null;
  feedback: string | null;
  status: SubmissionStatusType;
  gradedAt: Date | null;
  submittedAt: Date;
}

export interface SlideSubmissionFeedback {
  slideId: string;
  score: number | null;
  feedback: string | null;
  status: string;
  gradedAt: Date | null;
}

export interface QuizAttemptRecord {
  attemptId: string;
  lessonId: string;
  lessonTitle: string;
  score: number;
  passed: boolean;
  submittedAt: Date;
  answers: unknown;
}

// --- COURSE TAGGING TYPES ---

export interface TagCategory {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  sortOrder: number;
}

export interface Tag {
  id: string;
  categoryId: string;
  tenantId: string;
  name: string;
  color?: string;
  sortOrder: number;
}

export interface TagCategoryWithTags extends TagCategory {
  tags: Tag[];
}

// --- SKILLS MASTERY TYPES ---

export interface Skill {
  id: string;
  courseId: string;
  name: string;
  description?: string;
}

export interface SkillTag {
  id: string;
  skillId: string;
  lessonId: string;
  slideId?: string;
  weight: number;
}

export interface SkillMastery {
  skillId: string;
  skillName: string;
  skillDescription?: string;
  courseId: string;
  courseTitle: string;
  masteryLevel: number;
  evidenceCount: number;
  lastUpdated: Date | null;
}

export interface InstructorSkillOverview {
  skillId: string;
  skillName: string;
  courseId: string;
  courseTitle: string;
  avgMastery: number;
  studentsWithMastery: number;
  totalStudents: number;
}

// --- GAMIFICATION TYPES ---

export type BadgeCategory = 'achievement' | 'streak' | 'mastery' | 'points' | 'level' | 'course';
export type BadgeTier = 'bronze' | 'silver' | 'gold' | 'platinum';

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: BadgeCategory;
  tier: BadgeTier;
  sortOrder: number;
}

export interface UserBadge {
  id: string;
  badgeId: string;
  earnedAt: Date;
  badge: Badge;
}

export interface StreakInfo {
  currentStreak: number;
  longestStreak: number;
  totalActiveDays: number;
  lastActivityDate: string | null;
}

export interface GamificationProfile {
  streak: StreakInfo;
  badges: UserBadge[];
  level: number;
  totalPoints: number;
  pointsToNextLevel: number;
  nextLevelPoints: number;
}

export type GamificationNotificationType = 'badge_earned' | 'streak_milestone' | 'level_up';

export interface GamificationNotification {
  id: string;
  type: GamificationNotificationType;
  title: string;
  message: string;
  badge?: Badge;
  level?: number;
  streak?: number;
}

export type CourseType = 'standard' | 'utility';

export interface UtilityMetadata {
  seoTitle: string;
  seoDescription: string;
  targetKeywords: string[];
  parentCourseId?: string;
  ctaText: string;
  ctaDescription: string;
  leadMagnet?: {
    type: 'cheatsheet' | 'pdf' | 'code' | 'template';
    title: string;
    description: string;
  };
  ogImage?: string;
  schemaType: 'HowTo' | 'Article' | 'Course';
}
